/**
 * Recuperación ante "chunk obsoleto" tras un deploy.
 *
 * En una SPA con code-splitting (Vite + React.lazy), cuando se publica una
 * versión nueva el `rsync --delete` borra los assets con hash viejo. Un usuario
 * con la pestaña abierta en la versión anterior, al navegar a una pantalla lazy,
 * pide un chunk/CSS con hash viejo que ya no existe → 404 → el import dinámico
 * falla y burbujea hasta el ErrorBoundary (pantalla "Ocurrió un error").
 *
 * La cura correcta es recargar la página una sola vez: el nuevo index.html trae
 * los hashes vigentes. El guard por sessionStorage evita un bucle de recargas si
 * el asset realmente falta (deploy roto), dejando que el ErrorBoundary muestre
 * la pantalla amigable en ese caso.
 */

const RELOAD_AT_KEY = "prikly:stale-chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;

/** ¿El error corresponde a un chunk/CSS dinámico que no se pudo cargar? */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : String(error ?? "");
  return /(\bpreload\b|dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported|Loading chunk .* failed|Loading CSS chunk|error loading dynamically imported module)/i.test(
    message
  );
}

/**
 * Recarga la página una sola vez para traer los assets vigentes. Devuelve true
 * si disparó la recarga; false si está en cooldown (ya recargó hace poco) para
 * no entrar en bucle.
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || "0");
    const now = Date.now();
    if (last && now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_AT_KEY, String(now));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}
